'use client';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { routes } from '@lib/constants/routes-constant';
import { domains } from '@lib/constants/domains';
import { MainLogo } from '@modules/common/components/logo/main-logo';
import { MenuIcon } from '@modules/common/icons/menu';
import useSearchHandle from '@lib/hooks/use-search-handle';
import objectToURLSearchParams from '@lib/util/object-to-url-search-params';
import SearchIcon from '@modules/common/icons/search';
import CartDropdown from '@modules/layout/components/cart-dropdown';
import clsx from 'clsx';
import './header.scss';
import Link from 'next/link';
import { ErrorMessage } from '@modules/common/components/error-message';
import { useStore } from '@lib/context/store-context';
import { SearchLocationInput } from '@modules/common/components/search-location-input/search-location-input';
import { Menu, Transition } from '@headlessui/react';
import User from '@modules/common/icons/user';
import { LOGIN_VIEW, useAccount } from '@lib/context/account-context';
import { H1 } from '@modules/common/components/typography/H1';
import { H2 } from '@modules/common/components/typography/H2';
import { CartDropdownContext } from '@lib/context/cart-dropdown-context';
import {
  CARD_PROGRAM_DISPLAY,
  CARD_PROGRAMS, FLIP_GIVE,
} from '@lib/constants';
import TTDPixel from '@modules/common/components/yelp-pixel/yelp-pixel';
import { AutocompleteGoogle } from '@modules/common/components/search-location-input/AutocompleteGoogle';
import { getRedemptionCardInfo } from '@lib/data/order-confirm-handle';
import { getAffiliateByRef } from '@lib/data';
import { AffiliateLandingInterface } from 'types/affiliate';
import { useSearchService } from '@lib/context/search-service-context';
import { SearchService } from '@lib/constants/variants';
import { SearchServiceLogo } from '@modules/search-service-logo';
import { configurationPageServiceCardProgram } from '@lib/constants/gift-card-type-constants';
import { getReferral, setFGToken, setReferral } from '@lib/util/local-storage';

declare let window: any;
const PLACEHOLDER_SEARCH = 'Search for restaurants, spas, and salons';
const PLACEHOLDER_SPAS_AND_SALON = 'Search for a spa or salon';
const PLACEHOLDER_RESTAURANT = 'Search for a restaurant';
const PLACE_HOLDER_SEARCH_ONE_LINE = 'SEARCH FOR A FOOD TYPE, RESTAURANT, OR SALON';
const ERROR_MESSAGE = 'Please enter a location';
const TITLE_GIVE_TEXT = 'GIVE';
const TITLE_SELECT_TEXT = 'SELECT';
const DEFAULT_NUMBER_LOCATION = 5;
const RESTAURANT_NUMBER_LOCATION = 3;
const SPAS_SALONS_NUMBER_LOCATION = 2;
const DEFAULT_LOCATION = 'restaurants, spas, and salons';
const RESTAURANT_LOCATION = 'local restaurants';
const SPAS_SALONS_LOCATION = 'local spas and salons';
const PLACEHOLDER_SEARCH_SERVICE = "Search for a service provider";
const HEADER_DEFAULT = `Choose from over <span class="text-pink_drink font-bold">5 million</span> restaurants, spas, and salons`
const HEADER_RESTAURANT = `Choose from over <span class="text-pink_drink font-bold">2 million</span> local restaurants`;
const HEADER_SPA = `Choose from over <span class="text-pink_drink font-bold">3 million</span> local spas and salons`;
const HEADER_SERVICE = `Choose from over <span class="text-pink_drink font-bold">7 million</span> local service providers`

const Header = () => {
  const searchService = useSearchService();
  const { customer, retrievingCustomer } = useAccount();
  const { setLoading, setRef } = useStore();
  const router = useRouter();
  const pathname = usePathname();
  const termRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const categoryRef = useRef<HTMLInputElement>(null);
  const [_hasLocation, setHasLocation] = useState(false);
  const [placeHolderSearch, setPlaceHolderSearch] =
    useState(PLACEHOLDER_SEARCH);
  const [error, setError] = useState<string | null>(null);
  const [enableSearch, setEnableSearch] = useState<boolean>(true);
  const [businessLink, setBusinessLink] = useState<string>("/");
  const cartContext = useContext(CartDropdownContext);
  const [hideSearchLocation, setHideSearchLocation] = useState(false);
  const [hasMRA, setHasMRA] = useState(false);
  const [titleText, setTitleText] = useState(TITLE_GIVE_TEXT);
  const [numberLocation, setNumberLocation] = useState(DEFAULT_NUMBER_LOCATION);
  const [descriptionLocation, setdescriptionLocation] = useState(DEFAULT_LOCATION);
  const [chooseYourOwn, setChooseYourOwn] = useState<string>('');
  const [affiliateByRef, setAffiliateByRef] = useState<AffiliateLandingInterface>();

  TTDPixel();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = urlParams.get('ref');
    const fgToken = urlParams.get('fgtoken');
    const search = urlParams.get('search') ?? '';

    setReferral(referrer);
    setFGToken(fgToken);

    if(referrer === FLIP_GIVE && configurationPageServiceCardProgram[search]){
      router.push(`/businesses/let-them-choose?fgtoken=${fgToken}&ref=flip&search=${search}`);
    }

  }, [router]);

  const {
    location: locationParam,
    term: termParam,
    category: categoryParam,
    search: searchFor,
    gift_card_id,
    provisioned_card_id,
    ref
  } = useSearchHandle();

  const showSectionSearch = useMemo(() => {
    if (
      pathname !== routes.checkout &&
      pathname.indexOf(routes.orderConfirm) === -1 &&
      pathname.indexOf(routes.redemption) === -1 &&
      pathname.indexOf(routes.merchantInquiry) === -1 &&
      pathname.indexOf(routes.accountLogin) === -1 &&
      pathname.indexOf(routes.temporarily_unavailable) === -1 &&
      pathname.indexOf(routes.store_closed) === -1 &&
      pathname.indexOf(routes.profile) === -1 &&
      pathname.indexOf(routes.cart) === -1 &&
      pathname.indexOf('contact-us') === -1 &&
      pathname.indexOf('about') === -1 &&
      pathname.indexOf('faqs') === -1 &&
      pathname.indexOf('privacy-policy') === -1 &&
      pathname.indexOf('terms-and-conditions') === -1 &&
      pathname.indexOf('cardholder-agreement') === -1 &&
      pathname.indexOf('communications-agreement') === -1 &&
      pathname.indexOf('business-contact') === -1 &&
      pathname.indexOf(routes.account) === -1
    ) {
      return true;
    }
    return false;
  }, [pathname]);

  const hasHeaderWithoutSearch = useMemo(() => {
    if (
      pathname.indexOf(routes.redemption) !== -1 &&
      pathname.endsWith('preview')
    ) {
      return true;
    }
    return false;
  }, [pathname]);

  const hasHeaderWithoutMenu = useMemo(() => {
    if (
      (pathname.indexOf(routes.redemption) !== -1 &&
        !pathname.endsWith('preview')) ||
      pathname.indexOf(routes.temporarily_unavailable) !== -1 ||
      pathname.indexOf(routes.store_closed) !== -1 ||
      pathname.indexOf(routes.merchantInquiry) !== -1 ||
      pathname.indexOf(routes.accountLogin) !== -1
    ) {
      return false;
    }
    return true;
  }, [pathname]);

  const showSectionCartDropdown = useMemo(() => {
    if (
      pathname.indexOf(routes.redemption) === -1 &&
      pathname.indexOf(routes.merchantInquiry) === -1
    ) {
      return true;
    }
    return false;
  }, [pathname]);

  const showSectionIconUser = useMemo(() => {
    if (pathname.indexOf(routes.accountLogin) === -1
      && pathname.indexOf(routes.redemption) === -1
      && pathname.indexOf(routes.switchMerchant) === -1
      && pathname.indexOf(routes.temporarily_unavailable) === -1
      && pathname.indexOf(routes.store_closed) === -1
    ) {
      return true;
    }
    return false;
  }, [pathname]);

  const showGiveLocalGiftDescription = useMemo(() => {
    if (pathname.indexOf(routes.search) === -1 && pathname.indexOf(routes.businesses) === -1) {
      return true;
    }
    return false;
  }, [pathname]);

  const hasLoggin = useMemo(() => {
    return !retrievingCustomer && customer;
  }, [customer, retrievingCustomer]);

  const HeaderTag = useMemo(
    () => {
      if (pathname.indexOf(routes.businesses) !== -1)
        return H2;

      return H1;
    },
    [pathname]
  );

  const getSearchCategory = (searchBy: string | undefined) => {
    const temp = ('' + searchBy)
      .toLowerCase()
      .replace('search for a', '')
      .trim();
    switch (temp) {
      case 'restaurant':
        return 'restaurant';
      case 'spa or salon':
        return 'spa';
    }
    return '';
  };

  const getSearchPlaceHolder = () => {
    if (hideSearchLocation)
      return PLACE_HOLDER_SEARCH_ONE_LINE;

    const programValue = sessionStorage.getItem(CARD_PROGRAMS.KEY) ?? categoryParam;
    switch (programValue) {
      case CARD_PROGRAMS.RESTAURANT:
      case CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT:
        return PLACEHOLDER_RESTAURANT;
      case CARD_PROGRAMS.SPAS_AND_SALON:
      case CARD_PROGRAMS.CHOOSE_YOUR_OWN_SPAS_SALONS:
        return PLACEHOLDER_SPAS_AND_SALON;
      case CARD_PROGRAMS.CHOOSE_YOUR_OWN_SERVICES:
      case CARD_PROGRAMS.SERVICEPROVIDER:
        return PLACEHOLDER_SEARCH_SERVICE;
      default:
        return PLACEHOLDER_SEARCH;
    }
  };

  const handleSearch = (event?: any) => {
    event?.preventDefault();
    setError(null);
    if (!locationRef.current?.value) {
      return setError(ERROR_MESSAGE);
    }
    if (categoryRef?.current?.value) {
      const url = decodeURIComponent(location.href);
      if (!url.includes('&category=') && !url.includes('?search=')) {
        categoryRef.current.value = '';
      }
    }
    const searchParamsURL = new URLSearchParams(window.location.search);
    let category = searchParamsURL?.get('category');
    if (categoryRef?.current?.value) {
      category = categoryRef?.current?.value;
    }
    const params = {
      location: locationRef.current?.value,
      term: termRef.current?.value,
      category: category ?? "",
      enableSearch: enableSearch,
      sort_by: 'best_match'
    };

    const filteredParams: Record<string, any> = Object.fromEntries(
      Object.entries(params).filter(([_key, value]) => value !== '')
    );
    let urlSearch = `${routes.search}?${objectToURLSearchParams(
      filteredParams
    )}`;
    if (pathname === routes.switchMerchant) {
      let chooseYourOwnCategory = '';
      if (chooseYourOwn) {
        chooseYourOwnCategory = chooseYourOwn === CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT
          ? CARD_PROGRAM_DISPLAY.RESTAURANT
          : CARD_PROGRAM_DISPLAY.SPAS_AND_SALON;
        params.category = chooseYourOwnCategory;
      }
      urlSearch = `${routes.switchMerchant}?gift_card_id=${gift_card_id}&provisioned_card_id=${provisioned_card_id}&${objectToURLSearchParams(filteredParams)}`;
    }
    if (hasMRA) {
      urlSearch += `&ref=${affiliateByRef?.referrer_tag}`;
      const card_types = JSON.parse(affiliateByRef?.card_types ?? '');

      if (card_types?.length == 1 && card_types[0] == CARD_PROGRAMS.SPAS_AND_SALON) {
        urlSearch += `&category=${CARD_PROGRAM_DISPLAY.SPAS_AND_SALON}`
      }
    }
    const termString = termParam ?? '';
    if (locationParam !== params?.location || termString !== params?.term) {
      setLoading(true);
    }
    if (searchService?.currentSearch) {
      urlSearch += `&providerId=${searchService?.currentSearch}`;
    }
    router.push(urlSearch);
  };

  useEffect(() => {
    setPlaceHolderSearch(getSearchPlaceHolder);
    if (locationRef.current && !locationParam) {
      locationRef.current.value = '';
    } else if (
      locationRef.current &&
      locationParam &&
      locationRef.current.value !== locationParam
    ) {
      locationRef.current.value = locationParam;
    }
    if (termRef.current && !termParam) {
      termRef.current.value = '';
    } else if (
      termRef.current &&
      termParam &&
      termRef.current.value !== termParam
    ) {
      termRef.current.value = termParam;
    }
  }, [locationParam, termParam, categoryParam]);

  useEffect(() => {
    const getTransactionDetails = async () => {
      let response = await getRedemptionCardInfo(provisioned_card_id as string);
      if (response?.card_program) {
        switch (response.card_program) {
          case CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT:
          case CARD_PROGRAMS.RESTAURANT:
            setPlaceHolderSearch(PLACEHOLDER_RESTAURANT);
            setNumberLocation(RESTAURANT_NUMBER_LOCATION);
            setdescriptionLocation(RESTAURANT_LOCATION);
            setChooseYourOwn(CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT);
            break;
          case CARD_PROGRAMS.CHOOSE_YOUR_OWN_SPAS_SALONS:
          case CARD_PROGRAMS.SPAS_AND_SALON:
            setPlaceHolderSearch(PLACEHOLDER_SPAS_AND_SALON);
            setNumberLocation(SPAS_SALONS_NUMBER_LOCATION);
            setdescriptionLocation(SPAS_SALONS_LOCATION);
            setChooseYourOwn(CARD_PROGRAMS.CHOOSE_YOUR_OWN_SPAS_SALONS);
            break;
          case CARD_PROGRAMS.SERVICEPROVIDER:
            setPlaceHolderSearch(PLACEHOLDER_SEARCH_SERVICE);
            break;
          default:
            setChooseYourOwn('');
            break;
        }
      } else {
        setChooseYourOwn('');
      }
    };
    if (pathname === routes.switchMerchant) {
      setTitleText(TITLE_SELECT_TEXT);
      getTransactionDetails();
    }
    setError(null);
  }, [pathname]);

  useEffect(() => {
    setHideSearchLocation(pathname.indexOf(routes.businesses) !== -1)
  }, [pathname])

  useEffect(() => {
    setPlaceHolderSearch(getSearchPlaceHolder);
  }, [hideSearchLocation])

  useEffect(() => {
    const currentDomain = window.location.hostname;
    if (currentDomain === domains.dev) {
      setBusinessLink(domains.b2b_dev);
    }
    else if (currentDomain === domains.qa) {
      setBusinessLink(domains.b2b_qa)
    }
    else {
      setBusinessLink(domains.prod);
    }
  }, []);

  useEffect(() => {
    if (termRef?.current) {
      setPlaceHolderSearch(getSearchPlaceHolder);
    }

    if (!searchFor) return;
    const category = getSearchCategory(searchFor);
    if (categoryRef.current && category && categoryRef.current.value !== category) {
      categoryRef.current.value = category;
    }
  }, [searchFor]);

  useEffect(() => {
    const currentRef = ref ??  getReferral();
    if (currentRef) {
      const getAffiliate = async (currentRef: string) => {
        try {
          const res = await getAffiliateByRef(currentRef);
          if (res?.ok && res?.body) {
            setAffiliateByRef(res?.body);
            setRef(res?.body);
            setHasMRA(true);
          }
        } catch (error) {
          setHasMRA(false);
        }
      }
      getAffiliate(currentRef);
    }
  }, [ref, pathname]);

  useEffect(() => {
    if (locationRef?.current && hasMRA && affiliateByRef && affiliateByRef.restriction_type === "city") {
      locationRef.current.value = affiliateByRef?.city_restriction;
    }
    if (affiliateByRef?.card_types) {
      const card_types = JSON.parse(affiliateByRef?.card_types);
      if (card_types?.length > 2) {
        setPlaceHolderSearch(PLACEHOLDER_SEARCH);
      }
      if (card_types?.length == 1) {
        if (card_types[0] === 'restaurant') {
          setPlaceHolderSearch(PLACEHOLDER_RESTAURANT);
        } else {
          setPlaceHolderSearch(PLACEHOLDER_SPAS_AND_SALON);
          if (categoryRef?.current) {
            categoryRef.current.value = 'spa or salon'
          }
        }
      }
    }
  }, [hasMRA, affiliateByRef]);

  if (hasHeaderWithoutSearch) {
    return (
      <div className="header-withoutSearch">
        <div className="header-withoutSearch-bg"></div>
        <a href="/">
          <MainLogo className="header-logo" />
        </a>
        <div className="header-confetti header-confetti-noPrint">
          <img src={'/confetti.png'} alt="confetti" />
        </div>
      </div>
    );
  }

  const handleKeyDown = (event?: any) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default Enter key behavior
      handleSearch(event); // Trigger the form submission
    }
  };

  const handleClick = (event?: any) => {
    if (!hideSearchLocation)
      return;
    sessionStorage.setItem(CARD_PROGRAMS.KEY, CARD_PROGRAMS.CHOOSE_YOUR_OWN);
    setHideSearchLocation(false);
  };

  const getHeader = () => {
    if (pathname === routes.switchMerchant) {
      if (categoryParam === CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT ||
        categoryParam === CARD_PROGRAMS.RESTAURANT
      ) {
        return HEADER_RESTAURANT;
      }
      if (categoryParam === CARD_PROGRAMS.CHOOSE_YOUR_OWN_SPAS_SALONS ||
        categoryParam === CARD_PROGRAMS.SPAS_AND_SALON
      ) {
        return HEADER_SPA;
      }
      if (categoryParam === CARD_PROGRAMS.CHOOSE_YOUR_OWN_SERVICES ||
        categoryParam === CARD_PROGRAMS.SERVICEPROVIDER
      ) {
        return HEADER_SERVICE;
      }
    }
    return HEADER_DEFAULT;
  }

  return (
    <div className={clsx('inset-x-0 z-50 group bg-bumble_bee')}>
      <header className={'relative container-page pt-2 md:pt-4 z-10'}>
        <div
          className={clsx(
            'header-confetti',
            {
              'header-confetti--left': !hasHeaderWithoutMenu,
            },
            {
              'header-confetti--right': !showSectionIconUser,
            }
          )}
        >
          <img src={'/confetti.png'} alt="confetti" />
        </div>
        <div className="header-main">
          <div
            className={clsx('flex items-center flex-none md:w-[186px]', {
              'w-[77px]': showSectionIconUser,
            })}
          >
            {hasHeaderWithoutMenu && (
              <>
                <Menu as="div">
                  <div className="h-[21px]">
                    <Menu.Button
                      aria-label="button menu icon"
                      className="focus-visible::outline-0 focus:outline-0 outline-none"
                    >
                      <MenuIcon />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="focus-visible::outline-0 focus:outline-0 outline-none">
                      <div className="navbar focus-visible::outline-0">
                        <Menu.Item>
                          <Link
                            className="navbar-item navbar-item-bgGray"
                            href="/about"
                            prefetch={false}
                          >
                            About Us
                          </Link>
                        </Menu.Item>
                        <Menu.Item>
                          <Link
                            className="navbar-item"
                            href="/contact-us"
                            prefetch={false}
                          >
                            Contact Us
                          </Link>
                        </Menu.Item>
                        <Menu.Item>
                          <Link
                            href={businessLink + `/business-contact`}
                            prefetch={false}
                            className="navbar-item navbar-item-bgGray"
                          >
                            For Business
                          </Link>
                        </Menu.Item>
                        <Menu.Item>
                          <Link
                            className="navbar-item"
                            href="https://app.akimbocard.com/app/#/akimbo/cardBalance"
                            prefetch={false}
                            target="_blank"
                          >
                            Check Balance
                          </Link>
                        </Menu.Item>
                        <Menu.Item>
                          <Link
                            className="navbar-item navbar-item-bgGray"
                            href="/faqs"
                            prefetch={false}
                          >
                            FAQs
                          </Link>
                        </Menu.Item>
                        {searchService.hasGoogle && searchService.hasYelp && <>
                          {searchService?.currentSearch == SearchService.YELP && <Menu.Item>
                            <button
                              className="text-left navbar-item border-b border-[#bbb9b9]"
                              onClick={() => searchService.updateCurrentSearch(SearchService.GOOGLE)}
                              type='button'
                            >
                              Use Google Search
                            </button>
                          </Menu.Item>}
                          {searchService?.currentSearch == SearchService.GOOGLE && <Menu.Item>
                            <button
                              className="text-left navbar-item border-b border-[#bbb9b9]"
                              onClick={() => searchService.updateCurrentSearch(SearchService.YELP)}
                              type='button'
                            >
                              Use Yelp Search
                            </button>
                          </Menu.Item>}
                        </>}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
                {hasMRA && <button type="button" className='mra-btn'
                  style={{ backgroundColor: '#' + affiliateByRef?.button_color, color: '#' + affiliateByRef?.text_color }}
                  onClick={() => window.open(`${affiliateByRef?.url}`, '_blank', 'noopener,noreferrer')}>{
                    affiliateByRef?.button_text
                  }</button>}
              </>
            )}
          </div>

          <div className="flex w-full justify-center items-center min-h-[160px]">
            {hasMRA && <div className='mra-logo'>
              <Link
                href={affiliateByRef?.logo_redirect ?? '#'}
                prefetch={false} target={affiliateByRef?.logo_redirect ? '_blank' : ''}>
                <img src={affiliateByRef?.logo ?? ''} alt={affiliateByRef?.referrer_tag ?? ''} width="87" height={'120'} className="mt-2" />
              </Link>
            </div>}
            {(pathname !== routes.temporarily_unavailable && pathname !== routes.store_closed && pathname !== routes.home) ? (
              <Link
                href="/"
                prefetch={false}
                className={clsx({
                  'header-logo-margin':
                    showSectionIconUser && showSectionCartDropdown,
                })}
                onClick={() => {
                  sessionStorage.removeItem(
                    CARD_PROGRAMS.KEY
                  );
                }}
              >
                <MainLogo className="header-logo" />
              </Link>
            ) : (
              <div className={clsx({ 'header-logo-margin': false })}>
                <MainLogo className="header-logo" />
              </div>
            )}
          </div>
          <div
            className={clsx(
              'flex flex-none md:w-[186px] justify-end',
              { 'w-[77px]': showSectionIconUser }
            )}
          >
            {cartContext?.pointMode ? (
              <div className="mt-10 text-pink_drink select-none">
                <div className="w-28 border border-pink_drink rounded-2xl py-2 text-center bg-off_white font-medium">
                  <div>Total Points:</div>
                  <div>{cartContext.totalPoints.toLocaleString()} pts</div>
                </div>
              </div>
            ) : (
              <>
                {showSectionIconUser && (
                  <div
                    className={clsx('flex items-center', {
                      'w-5': !showSectionCartDropdown,
                    })}
                  >
                    {hasLoggin && (
                      <Link
                        href={routes.profile}
                        prefetch={false}
                        className="mr-1 object-cover"
                        aria-label="link user icon"
                      >
                        <User size={'1.8em'} />
                      </Link>
                    )}
                    {!hasLoggin && (
                      <>
                        {process.env.NEXT_PUBLIC_DISABLE_LOGIN_SIGNUP ===
                          'true' ? (
                          <div className="lg:w-[200px] md:w-[100px] sm:w-[50px]"></div>
                        ) : (
                          <div className="flex items-center">
                            <div className="flex-auto">
                              <Link
                                href={`${routes.accountLogin}?view=${LOGIN_VIEW.SIGN_IN}`}
                                prefetch={false}
                                className="userNav-btn mr-1"
                              >
                                LOGIN
                              </Link>
                              <Link
                                href={routes.accountLogin}
                                prefetch={false}
                                className="mr-1 object-cover userNav-icon"
                                aria-label="link user icon"
                              >
                                <User size={'1.8em'} />
                              </Link>
                            </div>
                            <div className="flex-auto sign-up-btn">
                              <Link
                                href={`${routes.accountLogin}?view=${LOGIN_VIEW.REGISTER}`}
                                prefetch={false}
                                className="userNav-btn userNav-btn--isPrimary"
                              >
                                SIGN UP
                              </Link>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {
                      <div
                        className={clsx('mt-0 h-7', {
                          'ml-2': showSectionIconUser,
                        })}
                      >
                        <CartDropdown />
                      </div>
                    }
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {showSectionSearch && (
          <>
            <div className="giveLocalGift">
              <HeaderTag className="lg:!text-[3rem] !font-black">
                {titleText} A <span className="text-pink_drink">LOCAL</span> GIFT
                CARD
              </HeaderTag>

              {showGiveLocalGiftDescription && (
                <div className="giveLocalGift-description">
                  {hasMRA ? <>{affiliateByRef?.description}</> : <><p dangerouslySetInnerHTML={{
                    __html: getHeader()

                  }}></p></>}
                </div>
              )}
              {hasMRA && <button type='button'
                className='font-semibold mra-link'
                onClick={() => window.open(`${affiliateByRef?.url}`, '_blank', 'noopener,noreferrer')}>
                {affiliateByRef?.button_text}
              </button>}
            </div>
            <div
              className={clsx(
                'mt-5',
                { 'pb-2': error },
                { 'pb-[18px]': !error }
              )}
            >
              <div className="">
                <form onSubmit={(e: any) => handleSearch(e)} role="search">
                  <div className="header-search" onClick={handleClick}>
                    <div className="header-input">
                      <button
                        type="button"
                        className="header-inputIcon"
                        onClick={(e) => handleSearch(e)}
                        aria-label="button search icon"
                      >
                        <SearchIcon size={20} />
                      </button>
                      <label
                        htmlFor="search_business"
                        aria-label="Business Search"
                        className="hidden"
                      >
                        Business Search:
                      </label>
                      <input
                        id="headerSearch"
                        aria-label="search business"
                        name="search_business"
                        type="search"
                        ref={termRef}
                        placeholder={placeHolderSearch}
                        onFocus={() => setHasLocation(true)}
                        onKeyDown={handleKeyDown}
                        autoComplete="off"
                      />
                      <input
                        aria-label="search business category hidden"
                        id="headerCategory"
                        type="hidden"
                        ref={categoryRef}
                        autoComplete="off"
                      />
                    </div>
                    {!hideSearchLocation && (
                      <div
                        className={clsx(
                          'header-input header-input-location header-input--isBorder',
                          { '!hidden': affiliateByRef?.restriction_type === 'city' }
                        )}
                      >
                        <span className="header-inputIcon"></span>
                        {!hasMRA ? <SearchLocationInput
                          ref={locationRef}
                          onSearch={handleSearch}
                          setEnableSearch={setEnableSearch}
                          hasMRA={hasMRA}
                        /> :
                          <AutocompleteGoogle
                            ref={locationRef}
                            onSearch={handleSearch}
                            setEnableSearch={setEnableSearch}
                            hasMRA={hasMRA}
                            affiliateByRef={affiliateByRef}
                          />
                        }
                      </div>)}
                  </div>
                  {error && (
                    <ErrorMessage className="!text-base mt-2">
                      {error}
                    </ErrorMessage>
                  )}
                  <div className="giveLocalGift-yelp">
                    <span>Powered by</span>
                    <SearchServiceLogo />
                  </div>
                  <button
                    type="submit"
                    className="hidden"
                    aria-label="submit"
                  />
                </form>
              </div>
            </div>
          </>
        )}
      </header>
    </div>
  );
};

export default Header;
